// Turn hold (bundle K) — keeps Alex from taking the turn during the candidate's short pauses.
//
// Interview 61: Alex started speaking after only 0.16–1.28 s of silence (turn eagerness "patient" was
// already set) and cut the candidate off 11 times. The ElevenLabs SDK's sendUserActivity() makes the
// agent "not attempt to speak for at least 2 seconds" (SDK 0.13.1 README). While the candidate's voice
// was heard in the last VOICE_RECENT_MS we send it every SEND_EVERY_MS, so Alex can only start
// ~VOICE_RECENT_MS + 2 s ≈ 2.5 s after the candidate stops. (61's real audio: 94% of the candidate's
// mid-answer pauses were shorter than 2.5 s; this would have held 18 of Alex's 19 interrupted starts.)
//
// Runs on plain timers and refs — no React state (the live call must never re-render).

export const VOICE_RECENT_MS = 500;        // + the SDK's ≥2 s → ~2.5 s total hold
export const SEND_EVERY_MS = 300;
export const SAMPLE_MS = 50;
export const VOICE_ABOVE_FLOOR_DB = 12;    // voice = this far above the tracked noise floor …
export const VOICE_MIN_DB = -55;           // … and above this absolute level (dBFS)

/**
 * Pure decision logic (unit-tested). Feed one level reading per sample; it calls send() when the
 * candidate is speaking (or just was) and Alex is not.
 */
export function createTurnHold({ send, isAgentSpeaking = () => false, now = () => Date.now() }) {
  let floor = -60;
  let lastVoice = -Infinity;
  let lastSent = -Infinity;
  let sends = 0;
  return {
    onLevel(db) {
      const t = now();
      // Noise floor: follows quiet levels down quickly, drifts up slowly (~0.4 dB/s at 50 ms samples)
      if (db < floor) floor = 0.8 * floor + 0.2 * db; else floor += 0.02;
      if (db > VOICE_MIN_DB && db > floor + VOICE_ABOVE_FLOOR_DB) lastVoice = t;
      if (isAgentSpeaking()) return false;              // Alex's own voice / candidate barging in: not ours
      if (t - lastVoice <= VOICE_RECENT_MS && t - lastSent >= SEND_EVERY_MS) {
        lastSent = t;
        sends += 1;
        try { send(); } catch (e) { /* never break the call */ }
        return true;
      }
      return false;
    },
    stats: () => ({ sends, floor: Math.round(floor) }),
  };
}

/** RMS level in dBFS of a Float32 time-domain buffer. */
export function levelDb(samples) {
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  return 10 * Math.log10(sum / Math.max(1, samples.length) + 1e-12);
}

/**
 * Start on the candidate's microphone stream. Returns { stop(), stats() }. Never throws — if Web Audio is
 * unavailable the interview simply runs without the hold.
 */
export function startTurnHold({ stream, send, isAgentSpeaking }) {
  try {
    if (!stream || !stream.getAudioTracks || stream.getAudioTracks().length === 0) return { stop() {}, stats: () => null };
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const hold = createTurnHold({ send, isAgentSpeaking });
    const timer = setInterval(() => {
      try { analyser.getFloatTimeDomainData(buf); hold.onLevel(levelDb(buf)); } catch (e) { /* ignore */ }
    }, SAMPLE_MS);
    return {
      stop() {
        clearInterval(timer);
        // close() returns a promise: a second stop() (call end + unmount) rejected it with
        // "InvalidStateError: Cannot close a closed AudioContext" — guard the state and swallow the rejection
        try { if (ctx.state !== 'closed') ctx.close().catch(() => {}); } catch (e) { /* ignore */ }
      },
      stats: hold.stats,
    };
  } catch (e) {
    return { stop() {}, stats: () => null };
  }
}
